import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { appointmentTypesService } from '../services/appointmentTypesService'
import { appointmentService } from '../services/appointmentService'
import { AppointmentTypeConfig, AppointmentTypeFormData, COLOR_PALETTES, COLOR_PALETTE_GROUPS, ColorPalette } from '../types/appointmentType'
import { AppointmentType, AppointmentFormData } from '../types/appointment'
import './SettingsAppointmentTypes.css'

export function SettingsAppointmentTypes() {
  const queryClient = useQueryClient()
  const { hasPermission } = useAuth()
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingType, setEditingType] = useState<AppointmentTypeConfig | null>(null)
  const [selectedPaletteGroup, setSelectedPaletteGroup] = useState<string | null>(null)
  const [formData, setFormData] = useState<AppointmentTypeFormData>({
    name: '',
    code: '',
    colors: {
      background: '#f3f4f6',
      border: '#d1d5db',
      hoverBackground: '#e5e7eb',
      hoverBorder: '#9ca3af',
    },
  })

  const { data: appointmentTypes, isLoading, error } = useQuery({
    queryKey: ['appointment-types'],
    queryFn: () => appointmentTypesService.getAll(),
  })

  const createMutation = useMutation({
    mutationFn: (data: AppointmentTypeFormData) => appointmentTypesService.create(data),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['appointment-types'] })
      setShowAddForm(false)
      resetForm()
      alert('Appointment type created successfully')
    },
    onError: (error: any) => {
      console.error('Failed to create appointment type:', error)
      alert('Failed to create appointment type. Please try again.')
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<AppointmentTypeFormData> }) => {
      // Get the appointment type being updated to compare old vs new values
      const typeToUpdate = appointmentTypes?.find((t) => t.id === id)
      if (!typeToUpdate) {
        throw new Error('Appointment type not found')
      }

      // Only process custom types (default types have restrictions)
      if (typeToUpdate.isDefault && data.code && data.code !== typeToUpdate.code) {
        throw new Error('Cannot change code of default appointment types')
      }

      // Track what changed
      const codeChanged = data.code && data.code !== typeToUpdate.code
      const nameChanged = data.name && data.name !== typeToUpdate.name
      const oldCode = typeToUpdate.code
      const oldName = typeToUpdate.name

      // Find all scheduled appointments with the old type code
      const allAppointments = await appointmentService.getAll()
      const appointmentsToUpdate = allAppointments.filter(
        (apt) => apt.type === oldCode && apt.status === 'scheduled'
      )

      // Update each appointment if code or name changed
      const updatePromises = appointmentsToUpdate.map(async (appointment) => {
        const updateData: Partial<AppointmentFormData> = {}
        let notePrefix = ''

        // If code changed, update the appointment type code
        if (codeChanged && data.code) {
          updateData.type = data.code as AppointmentType
        }

        // If name changed, add a note
        if (nameChanged) {
          const previousNote = appointment.notes || ''
          notePrefix = `[Appointment type changed] This appointment type was previously named "${oldName}". `
          const updatedNote = previousNote
            ? `${notePrefix}${previousNote}`
            : notePrefix.trim()
          updateData.notes = updatedNote
        }

        // Only update if there are changes
        if (Object.keys(updateData).length > 0) {
          await appointmentService.update(appointment.id, updateData)
        }
      })

      await Promise.all(updatePromises)

      // Now update the appointment type itself
      await appointmentTypesService.update(id, data)

      return {
        updatedCount: appointmentsToUpdate.length,
        typeName: data.name || oldName,
        codeChanged,
        nameChanged,
      }
    },
    onSuccess: async (result) => {
      await queryClient.refetchQueries({ queryKey: ['appointment-types'] })
      await queryClient.refetchQueries({ queryKey: ['appointments'] })
      setEditingType(null)
      resetForm()

      if (result.updatedCount > 0) {
        const changes = []
        if (result.codeChanged) changes.push('code')
        if (result.nameChanged) changes.push('name')
        alert(
          `Appointment type "${result.typeName}" updated successfully. ${result.updatedCount} scheduled appointment(s) were updated.`
        )
      } else {
        alert(`Appointment type "${result.typeName}" updated successfully.`)
      }
    },
    onError: (error: any) => {
      console.error('Failed to update appointment type:', error)
      alert(error?.message || 'Failed to update appointment type. Please try again.')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Get the appointment type being deleted to get its name and code
      const typeToDelete = appointmentTypes?.find((t) => t.id === id)
      if (!typeToDelete) {
        throw new Error('Appointment type not found')
      }

      // Only process custom types (default types shouldn't be deletable, but check anyway)
      if (typeToDelete.isDefault) {
        throw new Error('Cannot delete default appointment types')
      }

      // Find all scheduled appointments with this type
      const allAppointments = await appointmentService.getAll()
      const appointmentsToUpdate = allAppointments.filter(
        (apt) => apt.type === typeToDelete.code && apt.status === 'scheduled'
      )

      // Update each appointment: add note and change type to 'consultation'
      const updatePromises = appointmentsToUpdate.map(async (appointment) => {
        const previousNote = appointment.notes || ''
        const notePrefix = `[Appointment type changed] This appointment was previously "${typeToDelete.name}". `
        const updatedNote = previousNote
          ? `${notePrefix}${previousNote}`
          : notePrefix.trim()

        await appointmentService.update(appointment.id, {
          type: 'consultation',
          notes: updatedNote,
        })
      })

      await Promise.all(updatePromises)

      // Now delete the appointment type
      await appointmentTypesService.delete(id)

      return { updatedCount: appointmentsToUpdate.length, typeName: typeToDelete.name }
    },
    onSuccess: async (result) => {
      await queryClient.refetchQueries({ queryKey: ['appointment-types'] })
      await queryClient.refetchQueries({ queryKey: ['appointments'] })
      
      if (result.updatedCount > 0) {
        alert(
          `Appointment type "${result.typeName}" deleted successfully. ${result.updatedCount} scheduled appointment(s) were updated to "Consultation" with a note about the previous type.`
        )
      } else {
        alert(`Appointment type "${result.typeName}" deleted successfully.`)
      }
    },
    onError: (error: any) => {
      console.error('Failed to delete appointment type:', error)
      alert(error?.message || 'Failed to delete appointment type. Please try again.')
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      appointmentTypesService.toggleActive(id, isActive),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['appointment-types'] })
    },
    onError: (error: any) => {
      console.error('Failed to toggle appointment type status:', error)
      alert('Failed to update appointment type status. Please try again.')
    },
  })

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      colors: {
        background: '#f3f4f6',
        border: '#d1d5db',
        hoverBackground: '#e5e7eb',
        hoverBorder: '#9ca3af',
      },
    })
  }

  // Auto-generate code from name when creating a new appointment type
  useEffect(() => {
    // Only auto-generate when creating a new type (not editing)
    if (!editingType && formData.name) {
      // Convert name to code: lowercase, replace whitespace with hyphens
      const generatedCode = formData.name
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '') // Remove any non-alphanumeric characters except hyphens
        .replace(/-+/g, '-') // Replace multiple consecutive hyphens with single hyphen
        .replace(/^-|-$/g, '') // Remove leading/trailing hyphens

      // Always update the code when name changes during creation
      // User can still manually edit the code field if needed
      if (generatedCode) {
        setFormData((prev) => ({
          ...prev,
          code: generatedCode,
        }))
      }
    }
  }, [formData.name, editingType])

  const handleEdit = (type: AppointmentTypeConfig) => {
    setEditingType(type)
    setFormData({
      name: type.name,
      code: type.code,
      colors: type.colors,
    })
    setShowAddForm(true)
  }

  const handleDelete = (type: AppointmentTypeConfig) => {
    if (type.isDefault) {
      alert('Default appointment types cannot be deleted')
      return
    }

    if (window.confirm(`Are you sure you want to delete "${type.name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(type.id)
    }
  }

  const handleToggleActive = (type: AppointmentTypeConfig) => {
    toggleActiveMutation.mutate({ id: type.id, isActive: !type.isActive })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      alert('Please enter a name for the appointment type')
      return
    }

    if (!formData.code.trim()) {
      alert('Please enter a code for the appointment type')
      return
    }

    // Validate code format (lowercase, alphanumeric, hyphens)
    const codeRegex = /^[a-z0-9-]+$/
    if (!codeRegex.test(formData.code)) {
      alert('Code must be lowercase and contain only letters, numbers, and hyphens')
      return
    }

    // Check for duplicate code
    const normalizedCode = formData.code.trim().toLowerCase()
    const existingType = appointmentTypes?.find(
      (type) => type.code.toLowerCase() === normalizedCode
    )

    if (editingType) {
      // When editing, check if code conflicts with another type (not the current one)
      if (existingType && existingType.id !== editingType.id) {
        alert(`An appointment type with the code "${formData.code}" already exists. Please choose a different code.`)
        return
      }
      updateMutation.mutate({ id: editingType.id, data: formData })
    } else {
      // When creating, check if code already exists
      if (existingType) {
        alert(`An appointment type with the code "${formData.code}" already exists. Please choose a different code.`)
        return
      }
      createMutation.mutate(formData)
    }
  }

  const handleColorPaletteSelect = (palette: ColorPalette) => {
    setFormData({
      ...formData,
      colors: {
        background: palette.background,
        border: palette.border,
        hoverBackground: palette.hoverBackground,
        hoverBorder: palette.hoverBorder,
      },
    })
  }

  // Get palettes to display - either from selected group or all palettes
  const getDisplayPalettes = (): ColorPalette[] => {
    if (selectedPaletteGroup) {
      const group = COLOR_PALETTE_GROUPS.find((g) => g.name === selectedPaletteGroup)
      if (group && group.palettes && group.palettes.length > 0) {
        return group.palettes
      }
      // Fallback to all if group not found or empty
      return COLOR_PALETTES || []
    }
    return COLOR_PALETTES || []
  }

  const canWrite = hasPermission('settings:write')

  if (showAddForm) {
    return (
      <div className="settings-appointment-types">
        <div className="settings-header">
          <div>
            <h1>{editingType ? 'Edit' : 'Add'} Appointment Type</h1>
            <p className="settings-subtitle">
              {editingType ? 'Update appointment type details' : 'Create a new appointment type'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="appointment-type-form">
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Consultation, Follow-up"
              required
              disabled={!canWrite}
            />
          </div>

          <div className="form-group">
            <label htmlFor="code">Code *</label>
            <input
              id="code"
              type="text"
              value={formData.code}
              onChange={(e) => {
                const newCode = e.target.value
                  .toLowerCase()
                  .replace(/\s+/g, '-')
                  .replace(/[^a-z0-9-]/g, '') // Remove invalid characters
                  .replace(/-+/g, '-') // Replace multiple hyphens with single
                  .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
                setFormData({ ...formData, code: newCode })
              }}
              placeholder="e.g., consultation, follow-up"
              required
              disabled={!canWrite || editingType?.isDefault}
              pattern="[a-z0-9-]+"
            />
            <small>Lowercase letters, numbers, and hyphens only. Cannot be changed for default types.</small>
          </div>

          <div className="form-group">
            <label>Color Palette</label>
            <div className="palette-group-selector">
              <button
                type="button"
                className={`palette-group-btn ${selectedPaletteGroup === null ? 'active' : ''}`}
                onClick={() => setSelectedPaletteGroup(null)}
              >
                All ({COLOR_PALETTES?.length || 0})
              </button>
              {COLOR_PALETTE_GROUPS?.map((group) => (
                <button
                  key={group.name}
                  type="button"
                  className={`palette-group-btn ${selectedPaletteGroup === group.name ? 'active' : ''}`}
                  onClick={() => setSelectedPaletteGroup(group.name)}
                >
                  {group.name} ({group.palettes?.length || 0})
                </button>
              ))}
            </div>
            <div className="color-palettes">
              {getDisplayPalettes().length > 0 ? (
                getDisplayPalettes()
                  .filter((palette) => palette && palette.name)
                  .map((palette) => (
                    <button
                      key={palette.name}
                      type="button"
                      className="color-palette-btn"
                      onClick={() => handleColorPaletteSelect(palette)}
                      disabled={!canWrite}
                      title={palette.name}
                    >
                      <div
                        className="color-preview"
                        style={{
                          backgroundColor: palette.background,
                          borderColor: palette.border,
                        }}
                      >
                        <div
                          className="color-preview-hover"
                          style={{
                            backgroundColor: palette.hoverBackground,
                            borderColor: palette.hoverBorder,
                          }}
                        />
                      </div>
                      <span>{palette.name}</span>
                    </button>
                  ))
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                  No palettes available in this category
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Custom Colors</label>
            <div className="color-inputs">
              <div className="color-input-group">
                <label htmlFor="background">Background</label>
                <input
                  id="background"
                  type="color"
                  value={formData.colors.background}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      colors: { ...formData.colors, background: e.target.value },
                    })
                  }
                  disabled={!canWrite}
                />
                <input
                  type="text"
                  value={formData.colors.background}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      colors: { ...formData.colors, background: e.target.value },
                    })
                  }
                  disabled={!canWrite}
                  className="color-text-input"
                />
              </div>
              <div className="color-input-group">
                <label htmlFor="border">Border</label>
                <input
                  id="border"
                  type="color"
                  value={formData.colors.border}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      colors: { ...formData.colors, border: e.target.value },
                    })
                  }
                  disabled={!canWrite}
                />
                <input
                  type="text"
                  value={formData.colors.border}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      colors: { ...formData.colors, border: e.target.value },
                    })
                  }
                  disabled={!canWrite}
                  className="color-text-input"
                />
              </div>
              <div className="color-input-group">
                <label htmlFor="hoverBackground">Hover Background</label>
                <input
                  id="hoverBackground"
                  type="color"
                  value={formData.colors.hoverBackground}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      colors: { ...formData.colors, hoverBackground: e.target.value },
                    })
                  }
                  disabled={!canWrite}
                />
                <input
                  type="text"
                  value={formData.colors.hoverBackground}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      colors: { ...formData.colors, hoverBackground: e.target.value },
                    })
                  }
                  disabled={!canWrite}
                  className="color-text-input"
                />
              </div>
              <div className="color-input-group">
                <label htmlFor="hoverBorder">Hover Border</label>
                <input
                  id="hoverBorder"
                  type="color"
                  value={formData.colors.hoverBorder}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      colors: { ...formData.colors, hoverBorder: e.target.value },
                    })
                  }
                  disabled={!canWrite}
                />
                <input
                  type="text"
                  value={formData.colors.hoverBorder}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      colors: { ...formData.colors, hoverBorder: e.target.value },
                    })
                  }
                  disabled={!canWrite}
                  className="color-text-input"
                />
              </div>
            </div>
          </div>

          <div className="color-preview-large">
            <div
              className="preview-box"
              style={{
                backgroundColor: formData.colors.background,
                borderColor: formData.colors.border,
              }}
            >
              <div
                className="preview-box-hover"
                style={{
                  backgroundColor: formData.colors.hoverBackground,
                  borderColor: formData.colors.hoverBorder,
                }}
              />
              <span>Preview</span>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowAddForm(false)
                setEditingType(null)
                resetForm()
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={createMutation.isPending || updateMutation.isPending || !canWrite}
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : editingType
                ? 'Update'
                : 'Create'}
            </button>
          </div>
        </form>
      </div>
    )
  }

  return (
    <div className="settings-appointment-types">
      <div className="settings-header">
        <div>
          <h1>Appointment Types</h1>
          <p className="settings-subtitle">Manage appointment types and their color coding</p>
        </div>
        {canWrite && (
          <button
            className="btn-primary"
            onClick={() => {
              setShowAddForm(true)
              setEditingType(null)
              resetForm()
            }}
            aria-label="Add new appointment type"
          >
            + Add Appointment Type
          </button>
        )}
      </div>

      {isLoading && (
        <div className="loading" role="status" aria-live="polite">
          Loading appointment types...
        </div>
      )}

      {error && (
        <div className="error" role="alert" aria-live="assertive">
          Error loading appointment types
        </div>
      )}

      {!isLoading && !error && appointmentTypes && (
        <div className="appointment-types-list">
          <table className="appointment-types-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Colors</th>
                <th>Status</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {appointmentTypes.map((type) => (
                <tr key={type.id}>
                  <td>
                    <strong>{type.name}</strong>
                  </td>
                  <td>
                    <code>{type.code}</code>
                  </td>
                  <td>
                    <div className="color-display">
                      <div
                        className="color-swatch"
                        style={{
                          backgroundColor: type.colors.background,
                          borderColor: type.colors.border,
                        }}
                        title={`Background: ${type.colors.background}, Border: ${type.colors.border}`}
                      />
                      <span className="color-info">
                        {type.colors.background} / {type.colors.border}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${type.isActive ? 'active' : 'inactive'}`}>
                      {type.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {type.isDefault ? (
                      <span className="type-badge default">Default</span>
                    ) : (
                      <span className="type-badge custom">Custom</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      {canWrite && (
                        <>
                          <button
                            className="btn-edit"
                            onClick={() => handleEdit(type)}
                            title="Edit appointment type"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-toggle"
                            onClick={() => handleToggleActive(type)}
                            title={type.isActive ? 'Deactivate' : 'Activate'}
                            disabled={toggleActiveMutation.isPending}
                          >
                            {type.isActive ? '👁️' : '👁️‍🗨️'}
                          </button>
                          {!type.isDefault && (
                            <button
                              className="btn-delete"
                              onClick={() => handleDelete(type)}
                              title="Delete appointment type"
                              disabled={deleteMutation.isPending}
                            >
                              🗑️
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="settings-info">
        <h2>Appointment Type Management</h2>
        <div className="info-sections">
          <div className="info-section">
            <h3>Default Types</h3>
            <p>
              Default appointment types are system-provided and cannot be deleted. You can modify their colors and
              activate/deactivate them.
            </p>
          </div>
          <div className="info-section">
            <h3>Custom Types</h3>
            <p>
              You can create custom appointment types with your own names, codes, and color schemes. Custom types can be
              deleted if not in use.
            </p>
          </div>
          <div className="info-section">
            <h3>Color Coding</h3>
            <p>
              Each appointment type has a color scheme that appears in the calendar view. You can select from
              predefined palettes or customize the colors manually.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
